import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type EdgeId,
  type NodeId,
  type NodeSn,
  getRecordEntries,
  isNonNullable,
  isNullable,
  isPathLookupNodeData,
} from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { isPathLookupGraphMetadata } from 'components/Graph/GraphState';
import { array } from 'fp-ts';
import type { GraphData } from '../../model/GraphData';
import { addTrafficScoreToPacketDetail } from './helpers/addTrafficScoreToPacketDetail';
import { autoselectPacketDetail } from './helpers/autoselectPacketDetail';
import { findNodeIdByNodeSn } from './helpers/findNodeIdByNodeSn';
import { getAutoselectedPacketSource } from './helpers/getAutoselectedPacketSource';
import { getFollowingPacketIds } from './helpers/getFollowingPacketIds';
import { getPacketEdgeDetail } from './helpers/getPacketEdgeDetail';
import { hasOneIncomingPacket } from './helpers/hasOneIncomingPacket';
import { hasOneRemainingPacketToSelect } from './helpers/hasOneRemainingPacketToSelect';

import { sleep } from '@ipfabric/ui-components/utils/sleep';
import { findDeviceViewEntityByNodeSn } from 'components/DeviceView/helpers/findDeviceViewEntityByNodeSn';
import { highlightEdges } from 'components/Graph/commands/pathLookup/highlightEdges';
import { unhighlightEdges } from 'components/Graph/commands/pathLookup/unhighlightEdges';
import { EDGE_HIGHLIGHT_COLOR } from 'components/Graph/constants';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { pipe } from 'fp-ts/lib/pipeable';
import { changePathLookupPathInspectorActiveNode } from './changePathLookupPathInspectorActiveNode';
import { filterOutNonEdgePackets } from './helpers/filterOutNonEdgePackets';
import { getGeneratedPacketDetail } from './helpers/getGeneratedPacketDetail';
import { getNodeHighlightedEdges } from './helpers/getNodeHighlightedEdges';

// Find following edges in selected incoming edge.
const findFollowingPacketIds = (
  edgeId: EdgeId,
  nodeId: NodeId,
  graph: GraphData,
): EdgeId[] => {
  const foundEdge = graph.edges[edgeId];
  return getFollowingPacketIds(edgeId, foundEdge, graph.nodes[nodeId]) ?? [];
};

export interface InitPathLookupPathInspectorCommand {
  selectedIncomingEdgeId?: EdgeId;
  selectedOutgoingEdgeId?: EdgeId;
  nodeSn: NodeSn;
}

export const initPathLookupPathInspector: GraphCommandFactory<
  InitPathLookupPathInspectorCommand
> = (cmdContext, dispatch) => async (command) => {
  const {
    graph,
    graphMetadata,
    pathlookup: { pathInspector },
  } = cmdContext.getState();
  const deviceViewEntity = findDeviceViewEntityByNodeSn(
    cmdContext.getDeviceView().deviceViewEntities,
    command.nodeSn,
  );
  if (deviceViewEntity === null) {
    stumble('Unknown device view entity');
    return;
  }

  const nodeId = findNodeIdByNodeSn(graph, command.nodeSn);
  if (nodeId === null) {
    stumble('Unknown node ID. Node cannot be found');
    return;
  }
  const decision =
    isPathLookupGraphMetadata(graphMetadata) &&
    graphMetadata.decisions?.[nodeId];
  if (!decision) {
    return;
  }

  // Select path inspector detail if user clicked on already open node
  if (
    pathInspector.highlightedEdges[command.nodeSn] !== undefined &&
    pathInspector.activeNodeSn !== command.nodeSn &&
    deviceViewEntity !== undefined
  ) {
    dispatch(changePathLookupPathInspectorActiveNode, {
      deviceViewElement: deviceViewEntity.deviceViewElement,
      deviceViewEntityId: deviceViewEntity.id,
    });
    return;
  }

  const incomingEdges = array.flatten(Object.values(decision.trafficIn));

  // Wait for graph data after graph refresh if snapshot was changed
  await sleep(0);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  cmdContext.updateState((draft) => {
    const { activeNodeSn, highlightedEdges } = draft.pathlookup.pathInspector;
    const activeNodeHighlightedEdges = getNodeHighlightedEdges(
      activeNodeSn,
      highlightedEdges,
    );
    // Hide highlighted edges of previous node
    if (activeNodeHighlightedEdges !== undefined) {
      dispatch(unhighlightEdges, {
        edges: [
          ...activeNodeHighlightedEdges.incomingEdges,
          ...activeNodeHighlightedEdges.outgoingEdges,
        ],
        color: true,
      });
    }

    draft.pathlookup.pathInspector.activeNodeSn = command.nodeSn;
    draft.pathlookup.pathInspector.highlightedEdges[command.nodeSn] = {
      incomingEdges: [],
      outgoingEdges: [],
    };
    draft.pathlookup.pathInspector.selectedPackets[command.nodeSn] = {
      selectedIncomingPacket: null,
      selectedIncomingPacketSource: null,
      selectedOutgoingPacket: null,
      selectedOutgoingPacketSource: null,
    };
    draft.pathlookup.pathInspector.enlargedEdges[command.nodeSn] = [];
    const nodeHighlightedEdges =
      draft.pathlookup.pathInspector.highlightedEdges[command.nodeSn];
    const nodeSelectedPackets =
      draft.pathlookup.pathInspector.selectedPackets[command.nodeSn];
    if (
      nodeHighlightedEdges === undefined ||
      nodeSelectedPackets === undefined
    ) {
      return;
    }

    /**
     * Set first edge detail if there's only 1 incoming packet.
     * User does not have to select packet, it's selected automatically.
     */
    if (
      command.selectedIncomingEdgeId !== undefined ||
      hasOneIncomingPacket(incomingEdges, nodeId, graph)
    ) {
      const pathLookupNodeData = pipe(
        // Fixed noUncheckedIndexedAccess NIM-19408
        draft.graph.nodes[nodeId]!,
        nullable.chainPredicate(isPathLookupNodeData),
      );
      // If incoming edges is available, the one packet is from incoming edge. Select it and get edge detail.
      // Otherwise, it's generated packet and get generated packet detail.
      const edgeDetail =
        incomingEdges[0] !== undefined
          ? getPacketEdgeDetail(
              draft.graph,
              command.selectedIncomingEdgeId ?? incomingEdges[0],
            )
          : getGeneratedPacketDetail(
              ...getRecordEntries(
                pathLookupNodeData?.generatedPackets ?? {},
                // Fixed noUncheckedIndexedAccess NIM-19408
              )[0]!,
            );
      addTrafficScoreToPacketDetail(
        nodeSelectedPackets,
        draft.graph,
        edgeDetail,
      );
      if (edgeDetail !== null) {
        nodeSelectedPackets.selectedIncomingPacketSource =
          getAutoselectedPacketSource(draft.graph, nodeId, edgeDetail);
      }
    }

    // Autoselect second packet if there's only 1 incoming packet and 1 packet to select in output
    if (
      nodeSelectedPackets?.selectedIncomingPacket &&
      (command.selectedOutgoingEdgeId !== undefined ||
        hasOneRemainingPacketToSelect(
          draft.graph,
          nodeSelectedPackets.selectedIncomingPacket.id,
          nodeId,
        ))
    ) {
      const activeNodeId = findNodeIdByNodeSn(
        draft.graph,
        draft.pathlookup.pathInspector.activeNodeSn,
      );
      const packetDetail = autoselectPacketDetail(
        draft.graph,
        nodeSelectedPackets.selectedIncomingPacket.id,
        activeNodeId,
        command.selectedOutgoingEdgeId,
      );
      if (packetDetail !== null) {
        nodeSelectedPackets.selectedOutgoingPacket = packetDetail;
        nodeSelectedPackets.selectedOutgoingPacketSource =
          getAutoselectedPacketSource(draft.graph, nodeId, packetDetail);
      }
    }

    /**
     * If there are multiple incoming edges, highlight them.
     * User will have to manually choose first edge.
     */
    if (
      command.selectedIncomingEdgeId === undefined &&
      !hasOneIncomingPacket(incomingEdges, nodeId, graph)
    ) {
      nodeHighlightedEdges.incomingEdges = incomingEdges;
      dispatch(highlightEdges, {
        color: EDGE_HIGHLIGHT_COLOR,
        edges: incomingEdges,
      });
    }

    /**
     * If incoming edge is selected and there are multiple packets to select in output,
     * highlight following edges.
     */
    if (
      isNullable(nodeSelectedPackets.selectedOutgoingPacket) &&
      isNonNullable(nodeSelectedPackets.selectedIncomingPacket) &&
      !hasOneRemainingPacketToSelect(
        draft.graph,
        nodeSelectedPackets.selectedIncomingPacket.id,
        nodeId,
      )
    ) {
      const followingEdgeIds = pipe(
        findFollowingPacketIds(
          nodeSelectedPackets.selectedIncomingPacket.id,
          nodeId,
          draft.graph,
        ),
        filterOutNonEdgePackets(draft.graph),
      );
      if (followingEdgeIds === null) {
        return;
      }
      nodeHighlightedEdges.outgoingEdges = followingEdgeIds;
      dispatch(highlightEdges, {
        color: EDGE_HIGHLIGHT_COLOR,
        edges: followingEdgeIds,
      });
    }
  });
};
